import React, { useImperativeHandle, forwardRef } from "react";
import Editor from "@monaco-editor/react";
import options from "../utils/global";
import { updateInlinedCss } from "../services/editor";


const CodeEditor = forwardRef(({
  filename,
  code,
  language,
  setHasChanged,
  setCode,
  setWebMode,
  setIsFileExplorerVisible,
  onLineClick,
  saveFile,
  iframeRef
}, ref) =>  {
  const editorRef = React.useRef();

  useImperativeHandle(ref, () => ({
    searchContentAndScroll(searchContent) {
      const model = editorRef.current.getModel();
      const lineCount = model.getLineCount();

      for (let i = 1; i <= lineCount; i++) {
        const lineContent = model.getLineContent(i);
        if (lineContent.includes(searchContent)) {
          editorRef.current.revealLineInCenter(i);
          break;
        }
      }
    }
  }));

  function handleEditorDidMount(editor) {
    editorRef.current = editor;

    editor.onDidFocusEditorWidget(() => {
      setIsFileExplorerVisible(false);
      // You can perform additional actions here when the editor is focused
    });

    editor.onMouseDown((e) => {
      const position = e.target.position;
      if (position) {
        const lineNumber = position.lineNumber;
        const lineContent = editor.getModel().getLineContent(lineNumber);
        if (onLineClick) {
          onLineClick(lineContent);
        }
      }
    });
  }

  function updateSessionStorage(newCode) {
    saveFile(filename, newCode);
  }

  function updateChangedFiles() {
    const changedFiles =
      JSON.parse(sessionStorage.getItem("changedFiles")) || [];
    if (!changedFiles.includes(filename)) {
      changedFiles.push(filename);
      sessionStorage.setItem("changedFiles", JSON.stringify(changedFiles));
    }
  }

  function handleCodeChange(newCode) {
    updateSessionStorage(newCode);
    updateChangedFiles();

    if (!iframeRef?.current) return;

    try {
      const iframeDoc = iframeRef.current.contentWindow.document;

      if (filename.endsWith('.css')) {
        updateInlinedCss(iframeDoc, filename, newCode);
      }

      if (filename.endsWith('.html') || filename.endsWith('.htm')) {
        const parser = new DOMParser();
        const newDom = parser.parseFromString(newCode, 'text/html');
        iframeDoc.body.innerHTML = newDom.body.innerHTML;
      }
    } catch (err) {
      // ignore
    }
  }

  return (
    <div className="code-editor">
      <Editor
        className="main-editor"
        theme="vs-dark"
        options={options}
        path={filename}
        onMount={handleEditorDidMount}
        defaultLanguage={language}
        value={code}
        wordWrap={true}
        onChange={() => {
          setHasChanged(true);
          handleCodeChange(editorRef.current.getValue());
          setCode(editorRef.current.getValue());
          setWebMode(false);
        }}
      />
    </div>
  );
});

export default CodeEditor;
